<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>
    <div class="page-wrapper">
      <div class="content tax-master-page">
        <div class="page-header">
          <div class="page-title">
            <h4>HSN Tax Master</h4>
            <h6>Schedule tenant tax rate changes without editing individual items</h6>
          </div>
          <div class="header-actions">
            <button class="btn btn-submit" @click="openCreateModal">
              <vue-feather type="plus"></vue-feather>
              Add HSN/SAC
            </button>
            <button class="btn btn-light" :disabled="loading" @click="loadAll">
              <vue-feather type="refresh-cw"></vue-feather>
              Refresh
            </button>
          </div>
        </div>

        <div class="tax-toolbar">
          <b-form-input v-model.trim="filters.code" placeholder="Search HSN/SAC code" @keyup.enter="loadAll" />
          <b-form-select v-model="filters.code_type" :options="codeTypeOptions" />
          <b-form-checkbox v-model="filters.active_only" switch @change="loadAll">
            Active rates only
          </b-form-checkbox>
          <button class="btn btn-submit" :disabled="loading" @click="loadAll">
            <vue-feather type="search"></vue-feather>
            Search
          </button>
        </div>

        <div class="card">
          <div class="card-body">
            <div class="table-top">
              <div>
                <h5 class="mb-1">Rate Master</h5>
                <p class="mb-0 text-muted">Add HSN/SAC codes here first, then item forms can use those codes and auto-fill tax.</p>
              </div>
            </div>
            <div class="table-responsive">
              <table class="table tax-rate-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Code</th>
                    <th>Rate</th>
                    <th>Since</th>
                    <th>Status</th>
                    <th>Effective Window</th>
                    <th>Items</th>
                    <th class="text-end">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="rate in visibleRates"
                    :key="rate.id"
                    :class="{ 'selected-rate-row': selectedRateId === rate.id }"
                    @click="selectRate(rate)"
                  >
                    <td>
                      <span class="type-pill">{{ rate.code_type }}</span>
                    </td>
                    <td class="mono-text">{{ rate.code }}</td>
                    <td>
                      <strong>{{ formatPercent(rate.tax_percent) }}</strong>
                    </td>
                    <td>since {{ formatFriendlyDate(rate.effective_from) }}</td>
                    <td><span class="status-badge" :class="statusClass(rate)">{{ rateStatus(rate) }}</span></td>
                    <td>{{ formatWindow(rate) }}</td>
                    <td>{{ formatItems(rate.item_count) }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary" :disabled="!isOpenRate(rate)" @click.stop="openScheduleModal(rate)">
                        Change rate
                      </button>
                    </td>
                  </tr>
                  <tr v-if="!visibleRates.length">
                    <td colspan="8" class="text-muted">No tenant tax rates found. Add the HSN/SAC code here before assigning it to items.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div v-if="selectedTaxRate" class="card tax-breakdown-card">
          <div class="card-body">
            <div class="table-top">
              <div>
                <h5 class="mb-1">{{ selectedTaxRate.code_type }} {{ selectedTaxRate.code }}</h5>
                <p class="mb-0 text-muted">
                  {{ formatPercent(selectedTaxRate.tax_percent) }} {{ rateStatus(selectedTaxRate).toLowerCase() }}
                  since {{ formatFriendlyDate(selectedTaxRate.effective_from) }}
                </p>
              </div>
              <button class="btn btn-sm btn-outline-primary" :disabled="!isOpenRate(selectedTaxRate)" @click="openScheduleModal(selectedTaxRate)">
                Change rate
              </button>
            </div>
            <div class="breakdown-grid">
              <div v-for="part in selectedBreakdown" :key="part.label" class="breakdown-cell">
                <span>{{ part.label }}</span>
                <strong>{{ part.value }}</strong>
              </div>
              <div class="breakdown-cell">
                <span>Items</span>
                <strong>{{ formatItems(selectedTaxRate.item_count) }}</strong>
              </div>
              <div class="breakdown-cell wide">
                <span>Effective Window</span>
                <strong>{{ formatWindow(selectedTaxRate) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <div class="health-grid">
          <div v-for="panel in healthPanels" :key="panel.key" class="card health-card">
            <div class="card-body">
              <div class="health-title">
                <span>{{ panel.title }}</span>
                <strong>{{ health[panel.key]?.count || 0 }}</strong>
              </div>
              <div class="table-responsive compact-table">
                <table class="table">
                  <tbody>
                    <tr v-for="(row, index) in health[panel.key]?.rows || []" :key="index">
                      <td>{{ row.itemname || row.itemid || row.saleid || row.purchaseid }}</td>
                      <td class="text-end">{{ row.hsncode || row.cached_rate || row.salesitem_id || row.purchaseitem_id || "" }}</td>
                    </tr>
                    <tr v-if="!(health[panel.key]?.rows || []).length">
                      <td colspan="2" class="text-muted">No issues found</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div v-if="showModal" class="schedule-backdrop" @click.self="closeScheduleModal">
          <div class="schedule-modal">
            <div class="schedule-header">
              <div>
                <h5>{{ modalTitle }}</h5>
                <p>{{ modalSubtitle }}</p>
              </div>
              <button class="icon-button" type="button" title="Close" @click="closeScheduleModal">
                <vue-feather type="x"></vue-feather>
              </button>
            </div>

            <div class="schedule-body">
              <div v-if="modalMode === 'change'" class="current-rate-strip">
                <span>Current rate</span>
                <strong>{{ formatPercent(selectedRate.tax_percent) }}</strong>
              </div>

              <div v-if="modalMode === 'create'" class="form-group">
                <label>Code type</label>
                <b-form-select v-model="scheduleForm.code_type" :options="createCodeTypeOptions" />
              </div>

              <div v-if="modalMode === 'create'" class="form-group">
                <label>{{ scheduleForm.code_type === "SAC" ? "SAC code" : "HSN code" }}</label>
                <b-form-input v-model.trim="scheduleForm.code" placeholder="e.g. 09024090" />
              </div>

              <div class="form-group">
                <label>{{ modalMode === "create" ? "Tax rate" : "New tax rate" }}</label>
                <b-form-input v-model.number="scheduleForm.tax_percent" type="number" min="0" max="100" step="0.01" />
              </div>

              <div class="form-group">
                <label>Effective from</label>
                <b-form-input v-model="scheduleForm.effective_from" type="date" />
              </div>

              <div class="preview-box" :class="{ muted: !previewText }">
                {{ previewText || "Enter a new rate and effective date to preview the schedule." }}
              </div>
            </div>

            <div class="schedule-actions">
              <button class="btn btn-light" type="button" @click="closeScheduleModal">Cancel</button>
              <button class="btn btn-submit" type="button" :disabled="saving || !canSchedule" @click="saveRate">
                {{ saving ? "Saving..." : modalMode === "create" ? "Add rate" : "Schedule change" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "taxrates",
  data() {
    return {
      loading: false,
      saving: false,
      rates: [],
      health: {},
      showModal: false,
      modalMode: "change",
      selectedRate: {},
      selectedRateId: null,
      filters: {
        code: "",
        code_type: "",
        active_only: false,
      },
      scheduleForm: {
        code_type: "HSN",
        code: "",
        tax_percent: null,
        effective_from: "",
      },
      codeTypeOptions: [
        { value: "", text: "All types" },
        { value: "HSN", text: "HSN" },
        { value: "SAC", text: "SAC" },
      ],
      healthPanels: [
        { key: "missing_tax_code", title: "Missing HSN/SAC" },
        { key: "manual_tax_rate", title: "Manual Rates" },
        { key: "tax_rate_drift", title: "Rate Drift" },
        { key: "sales_interstate_mismatch", title: "Sales Split Mismatch" },
        { key: "purchase_interstate_mismatch", title: "Purchase Split Mismatch" },
      ],
    };
  },
  computed: {
    visibleRates() {
      return this.rates;
    },
    selectedTaxRate() {
      return this.rates.find((rate) => rate.id === this.selectedRateId) || this.rates[0] || null;
    },
    selectedBreakdown() {
      if (!this.selectedTaxRate) return [];
      const rate = Number(this.selectedTaxRate.tax_percent || 0);
      return [
        { label: "GST", value: this.formatPercent(rate) },
        { label: "CGST", value: this.formatPercent(Number(this.selectedTaxRate.cgstrate ?? rate / 2)) },
        { label: "SGST", value: this.formatPercent(Number(this.selectedTaxRate.sgstrate ?? rate / 2)) },
        { label: "IGST", value: this.formatPercent(Number(this.selectedTaxRate.igstrate ?? rate)) },
      ];
    },
    createCodeTypeOptions() {
      return this.codeTypeOptions.filter((option) => option.value);
    },
    canSchedule() {
      const hasCode = this.modalMode === "change" || String(this.scheduleForm.code || "").trim();
      return (
        (this.modalMode === "create" || this.selectedRate.id) &&
        hasCode &&
        this.scheduleForm.tax_percent !== null &&
        this.scheduleForm.tax_percent !== "" &&
        Number(this.scheduleForm.tax_percent) >= 0 &&
        Number(this.scheduleForm.tax_percent) <= 100 &&
        Boolean(this.scheduleForm.effective_from)
      );
    },
    modalTitle() {
      return this.modalMode === "create" ? "Add HSN/SAC Rate" : "Schedule Rate Change";
    },
    modalSubtitle() {
      if (this.modalMode === "create") return "Create the first active tax rate for a code";
      return `${this.selectedRate.code_type} ${this.selectedRate.code}`;
    },
    previewText() {
      if (!this.canSchedule) return "";
      if (this.modalMode === "create") {
        return `${this.scheduleForm.code_type} ${this.scheduleForm.code} will use ${this.formatPercent(this.scheduleForm.tax_percent)} from ${this.formatFriendlyDate(this.scheduleForm.effective_from)}. New items can select this code and pick up the rate automatically.`;
      }
      const previousDay = this.addDays(this.scheduleForm.effective_from, -1);
      const itemText = Number(this.selectedRate.item_count || 0) === 1 ? "item" : "items";
      return `${this.formatPercent(this.selectedRate.tax_percent)} stays in effect through ${this.formatFriendlyDate(previousDay)}. ${this.formatPercent(this.scheduleForm.tax_percent)} takes over ${this.formatFriendlyDate(this.scheduleForm.effective_from)} - every item on this code moves automatically. ${this.selectedRate.item_count || 0} ${itemText} currently use this code.`;
    },
  },
  created() {
    this.loadAll();
  },
  methods: {
    async loadAll() {
      this.loading = true;
      try {
        const params = {
          active_only: this.filters.active_only,
        };
        if (this.filters.code_type) params.code_type = this.filters.code_type;
        if (this.filters.code) params.code = this.filters.code;

        const [rates, health] = await Promise.all([
          axios.get("/v1/tax/rates", { params }),
          axios.get("/v1/tax/health"),
        ]);
        this.rates = rates.data || [];
        this.health = health.data || {};
        if (!this.rates.some((rate) => rate.id === this.selectedRateId)) {
          this.selectedRateId = this.rates[0]?.id || null;
        }
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not load tax master", text: error.response?.data?.detail || error.message });
      } finally {
        this.loading = false;
      }
    },
    selectRate(rate) {
      this.selectedRateId = rate.id;
    },
    openCreateModal() {
      this.modalMode = "create";
      this.selectedRate = {};
      this.scheduleForm = {
        code_type: this.filters.code_type || "HSN",
        code: this.filters.code || "",
        tax_percent: null,
        effective_from: new Date().toISOString().slice(0, 10),
      };
      this.showModal = true;
    },
    openScheduleModal(rate) {
      this.modalMode = "change";
      this.selectedRate = rate;
      this.scheduleForm = {
        code_type: rate.code_type,
        code: rate.code,
        tax_percent: null,
        effective_from: this.defaultEffectiveDate(),
      };
      this.showModal = true;
    },
    closeScheduleModal() {
      if (this.saving) return;
      this.showModal = false;
      this.selectedRate = {};
    },
    saveRate() {
      if (this.modalMode === "create") return this.createRate();
      return this.scheduleRateChange();
    },
    async createRate() {
      if (!this.canSchedule) return;
      this.saving = true;
      try {
        await axios.post("/v1/tax/rates", {
          code_type: this.scheduleForm.code_type,
          code: String(this.scheduleForm.code || "").trim(),
          tax_percent: Number(this.scheduleForm.tax_percent),
          effective_from: this.scheduleForm.effective_from,
        });
        this.closeScheduleModal();
        await this.loadAll();
        Swal.fire({
          icon: "success",
          title: "Tax rate added",
          text: `${this.scheduleForm.code_type} ${this.scheduleForm.code} is ready for item setup.`,
          timer: 1800,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not add rate", text: error.response?.data?.detail || error.message });
      } finally {
        this.saving = false;
      }
    },
    async scheduleRateChange() {
      if (!this.canSchedule) return;
      this.saving = true;
      try {
        const response = await axios.post("/v1/tax/rates/change", {
          code_type: this.selectedRate.code_type,
          code: this.selectedRate.code,
          tax_percent: Number(this.scheduleForm.tax_percent),
          effective_from: this.scheduleForm.effective_from,
          reason: "Scheduled from HSN Tax Master",
        });
        this.closeScheduleModal();
        await this.loadAll();
        Swal.fire({
          icon: "success",
          title: "Rate change scheduled",
          text: `${response.data.items_affected || 0} items will use the new rate from ${this.formatFriendlyDate(response.data.effective_from)}.`,
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (error) {
        Swal.fire({ icon: "error", title: "Could not schedule rate", text: error.response?.data?.detail || error.message });
      } finally {
        this.saving = false;
      }
    },
    formatPercent(value) {
      const numberValue = Number(value || 0);
      return `${Number.isInteger(numberValue) ? numberValue : numberValue.toFixed(2)}%`;
    },
    formatWindow(rate) {
      const from = this.formatFriendlyDate(rate.effective_from);
      const to = rate.effective_to ? this.formatFriendlyDate(this.addDays(rate.effective_to, -1)) : "Current";
      return `${from} to ${to}`;
    },
    formatItems(value) {
      const count = Number(value || 0);
      return `${count} ${count === 1 ? "item" : "items"}`;
    },
    formatFriendlyDate(value) {
      if (!value) return "";
      const date = new Date(`${value}T00:00:00`);
      return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    },
    defaultEffectiveDate() {
      const date = new Date();
      date.setDate(date.getDate() + 1);
      return date.toISOString().slice(0, 10);
    },
    addDays(value, days) {
      const date = new Date(`${value}T00:00:00`);
      date.setDate(date.getDate() + days);
      return date.toISOString().slice(0, 10);
    },
    isOpenRate(rate) {
      return !rate.effective_to;
    },
    isCurrentRate(rate) {
      const today = new Date().toISOString().slice(0, 10);
      return rate.effective_from <= today && (!rate.effective_to || rate.effective_to > today);
    },
    rateStatus(rate) {
      if (rate.status) return rate.status;
      const today = new Date().toISOString().slice(0, 10);
      if (rate.effective_from > today) return "Scheduled";
      if (rate.effective_to && rate.effective_to <= today) return "History";
      return "Active";
    },
    statusClass(rate) {
      return `status-${this.rateStatus(rate).toLowerCase()}`;
    },
  },
};
</script>

<style scoped>
.tax-master-page .page-header {
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tax-toolbar {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 160px 180px auto;
  gap: 10px;
  align-items: center;
  margin-bottom: 16px;
}

.tax-rate-table th,
.tax-rate-table td {
  vertical-align: middle;
}

.type-pill,
.status-badge {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  font-weight: 700;
}

.type-pill {
  background: #e9f7ef;
  color: #117a37;
}

.status-badge {
  background: #f4f6f8;
  color: #667085;
}

.status-active {
  background: #e9f7ef;
  color: #117a37;
}

.status-scheduled {
  background: #fff7e6;
  color: #9a5b00;
}

.status-history {
  background: #f4f6f8;
  color: #667085;
}

.tax-rate-table tbody tr {
  cursor: pointer;
}

.selected-rate-row {
  background: #f8fbff;
  box-shadow: inset 3px 0 0 #2563eb;
}

.tax-breakdown-card {
  margin-top: 16px;
}

.breakdown-grid {
  display: grid;
  grid-template-columns: repeat(6, minmax(120px, 1fr));
  gap: 10px;
  margin-top: 14px;
}

.breakdown-cell {
  border: 1px solid #edf0f2;
  border-radius: 8px;
  padding: 12px;
  min-height: 74px;
}

.breakdown-cell span,
.breakdown-cell strong {
  display: block;
}

.breakdown-cell span {
  color: #667085;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 6px;
}

.breakdown-cell strong {
  color: #111827;
  font-size: 16px;
}

.breakdown-cell.wide {
  grid-column: span 2;
}

.health-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
}

.health-card {
  min-height: 150px;
}

.health-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  margin-bottom: 8px;
}

.health-title strong {
  background: #eef2ff;
  border-radius: 6px;
  color: #3346a8;
  padding: 3px 8px;
}

.compact-table {
  max-height: 160px;
  overflow: auto;
}

.mono-text {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.schedule-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.45);
  padding: 16px;
}

.schedule-modal {
  width: min(520px, 100%);
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.18);
}

.schedule-header,
.schedule-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px;
}

.schedule-header {
  border-bottom: 1px solid #edf0f2;
}

.schedule-header h5,
.schedule-header p {
  margin: 0;
}

.schedule-header p {
  color: #667085;
  margin-top: 4px;
}

.schedule-body {
  padding: 20px;
}

.schedule-actions {
  border-top: 1px solid #edf0f2;
  justify-content: flex-end;
}

.icon-button {
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 6px;
  background: #f4f6f8;
  color: #1f2937;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.current-rate-strip,
.preview-box {
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 16px;
}

.current-rate-strip {
  display: flex;
  justify-content: space-between;
  background: #f8fafc;
  color: #475467;
}

.current-rate-strip strong {
  color: #111827;
}

.form-group {
  margin-bottom: 14px;
}

.form-group label {
  display: block;
  font-weight: 600;
  margin-bottom: 6px;
}

.preview-box {
  background: #fff7e6;
  color: #7a4b00;
  line-height: 1.45;
}

.preview-box.muted {
  background: #f8fafc;
  color: #667085;
}

@media (max-width: 1200px) {
  .breakdown-grid {
    grid-template-columns: repeat(3, minmax(120px, 1fr));
  }

  .health-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 800px) {
  .tax-toolbar,
  .breakdown-grid,
  .health-grid {
    grid-template-columns: 1fr;
  }

  .breakdown-cell.wide {
    grid-column: auto;
  }
}
</style>
